import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, authedUrl } from "@/lib/api";
import { SectionCard, KpiCard } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Trophy, Users, Briefcase, Zap, Award, TrendingUp, CheckCircle2, Medal } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const RANK_COLORS = ["#F5C842", "#B5B5B5", "#B87333"]; // gold, silver, bronze
const STATUS_COLORS = { todo: "#94a3b8", in_progress: "#3b82f6", code_review: "#a855f7", testing: "#f59e0b", completed: "#10b981" };

function RankBadge({ rank }) {
  const color = RANK_COLORS[rank - 1];
  if (color) return <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-black" style={{ backgroundColor: color }} data-testid={`rank-${rank}`}>{rank}</div>;
  return <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold bg-secondary" data-testid={`rank-${rank}`}>{rank}</div>;
}

function MemberRow({ member, rank }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border/40 last:border-0" data-testid={`lb-row-${member.id}`}>
      <RankBadge rank={rank} />
      <Avatar className="h-9 w-9"><AvatarImage src={authedUrl(member.avatar_url)} /><AvatarFallback className="text-[10px]">{(member.full_name||"?").split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{member.full_name}</div>
        <div className="text-[10px] text-muted-foreground truncate">{member.designation}</div>
      </div>
      <div className="text-right hidden md:block">
        <div className="text-[10px] text-muted-foreground">Tasks</div>
        <div className="text-sm">{member.tasks_completed}/{member.tasks_total}</div>
      </div>
      <div className="text-right hidden md:block">
        <div className="text-[10px] text-muted-foreground">Utilization</div>
        <div className={`text-sm ${member.utilization > 90 ? "text-red-400" : member.utilization < 60 ? "text-amber-400" : ""}`}>{member.utilization}%</div>
      </div>
      <div className="text-right w-24">
        <div className="text-[10px] text-muted-foreground">Score</div>
        <div className="text-sm font-semibold">{member.composite_score}</div>
      </div>
    </div>
  );
}

export default function TeamDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/teams/${id}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Failed to load"));
  }, [id]);

  if (error) return <div className="p-8"><Button variant="ghost" onClick={() => nav("/teams")}><ArrowLeft className="w-4 h-4 mr-1"/>Back</Button><div className="mt-4 text-red-500 text-sm">{error}</div></div>;
  if (!data) return <div className="p-8 text-muted-foreground">Loading team analytics…</div>;

  const { team, members, projects, leaderboard, metrics, task_status } = data;
  const lead = members.find((m) => m.id === team.lead_id);
  const topPerformers = leaderboard.slice(0, 3);
  const chartData = leaderboard.slice(0, 10).map((m) => ({ name: (m.full_name || "").split(" ")[0], score: m.composite_score, productivity: m.productivity_score, tasks: m.tasks_completed }));
  const pieData = task_status.map((s) => ({ name: s.status.replace("_", " "), value: s.count, color: STATUS_COLORS[s.status] || "#94a3b8" }));

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => nav("/teams")} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1"/>Back to teams</Button>

      {/* Hero card */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{team.department}</div>
            <h1 className="font-display font-semibold text-3xl tracking-tighter mt-1" data-testid="team-title">{team.name}</h1>
            {team.description && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{team.description}</p>}
            {lead && (
              <div className="flex items-center gap-2 mt-4">
                <div className="text-[10px] uppercase text-muted-foreground">Lead</div>
                <Avatar className="h-6 w-6"><AvatarImage src={authedUrl(lead.avatar_url)} /><AvatarFallback className="text-[9px]">{(lead.full_name||"?").split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                <span className="text-sm">{lead.full_name}</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="gap-1"><Users className="w-3 h-3"/>{metrics.member_count} members</Badge>
            <Badge variant="outline" className="gap-1"><Briefcase className="w-3 h-3"/>{metrics.project_count} projects</Badge>
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard testid="kpi-productivity" title="Avg Productivity" value={`${metrics.avg_productivity}%`} icon={TrendingUp} />
        <KpiCard testid="kpi-attendance" title="Avg Attendance" value={`${metrics.avg_attendance}%`} icon={CheckCircle2} />
        <KpiCard testid="kpi-utilization" title="Avg Utilization" value={`${metrics.avg_utilization}%`} icon={Zap} />
        <KpiCard testid="kpi-velocity" title="Avg Velocity" value={metrics.avg_velocity} icon={Award} />
      </div>

      {/* Podium */}
      {topPerformers.length > 0 && (
        <SectionCard title="Team Podium" subtitle="Top 3 performers based on composite score" icon={Trophy}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {topPerformers.map((m, i) => (
              <div key={m.id} className="rounded-xl hairline p-4 flex flex-col items-center text-center" data-testid={`podium-${i+1}`}>
                <div className="mb-2"><Medal className="w-6 h-6" style={{ color: RANK_COLORS[i] }} /></div>
                <Avatar className="h-14 w-14 mb-2"><AvatarImage src={authedUrl(m.avatar_url)} /><AvatarFallback>{(m.full_name||"?").split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                <div className="font-medium text-sm">{m.full_name}</div>
                <div className="text-[10px] text-muted-foreground mb-2">{m.designation}</div>
                <div className="text-3xl font-display font-semibold tracking-tighter" style={{ color: RANK_COLORS[i] }}>{m.composite_score}</div>
                <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Score</div>
                <div className="mt-3 w-full space-y-1">
                  <div className="flex justify-between text-[10px]"><span>Productivity</span><span>{m.productivity_score}%</span></div>
                  <Progress value={m.productivity_score} className="h-1" />
                  <div className="flex justify-between text-[10px]"><span>Tasks Done</span><span>{m.tasks_completed}/{m.tasks_total}</span></div>
                  <Progress value={m.tasks_total > 0 ? (m.tasks_completed/m.tasks_total)*100 : 0} className="h-1" />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <Tabs defaultValue="leaderboard">
        <TabsList>
          <TabsTrigger value="leaderboard" data-testid="tab-lb">Leaderboard</TabsTrigger>
          <TabsTrigger value="charts" data-testid="tab-charts">Charts</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-proj">Projects</TabsTrigger>
        </TabsList>

        <TabsContent value="leaderboard" className="mt-4">
          <SectionCard title="Full Team Ranking" subtitle="Ranked by composite performance score">
            {leaderboard.length === 0 ? (
              <div className="text-sm text-muted-foreground p-6 text-center">No members yet.</div>
            ) : (
              <div>{leaderboard.map((m, i) => <MemberRow key={m.id} member={m} rank={i+1} />)}</div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="charts" className="mt-4 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <SectionCard title="Top 10 by Score" subtitle="Composite score vs raw productivity">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="score" fill="#3b82f6" radius={[4,4,0,0]} name="Composite" />
                  <Bar dataKey="productivity" fill="#10b981" radius={[4,4,0,0]} name="Productivity" />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>
            <SectionCard title="Task Status Mix" subtitle={`${metrics.tasks_total} tasks across the team`}>
              {pieData.length === 0 ? (
                <div className="text-sm text-muted-foreground p-6 text-center">No tasks assigned yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </SectionCard>
          </div>
          <SectionCard title="Completed Tasks — Top 10">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="tasks" fill="#a855f7" radius={[0,4,4,0]} name="Completed tasks" />
              </BarChart>
            </ResponsiveContainer>
          </SectionCard>
        </TabsContent>

        <TabsContent value="projects" className="mt-4">
          <SectionCard title="Projects" subtitle="Active engagements owned by this team">
            {projects.length === 0 ? (
              <div className="text-sm text-muted-foreground p-6 text-center">This team isn&apos;t assigned to any project yet.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {projects.map((p) => (
                  <button key={p.id} onClick={() => nav(`/projects/${p.id}`)} className="text-left rounded-xl hairline p-4 hover:bg-accent/50 transition-colors" data-testid={`team-proj-${p.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium">{p.name}</div>
                      <Badge variant="outline" className="text-[10px] capitalize">{p.status}</Badge>
                    </div>
                    <div className="text-[10px] text-muted-foreground">Client · {p.client}</div>
                    <Progress value={p.progress || 0} className="h-1 mt-3" />
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>Progress</span><span>{Math.round(p.progress || 0)}%</span></div>
                  </button>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
